// client/src/components/inventory/SuppliersTab.tsx
import { Truck, Phone, Mail, MapPin, DollarSign, TrendingUp, FileText, Trash2, Edit, MessageSquare, Bell } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useState, useEffect } from "react";  
import { getShopSuppliers, getSuppliersAnalysis, deleteSupplier, exportSuppliersCSV, exportSuppliersWhatsApp } from "@/lib/api";  
import { useToast } from "@/hooks/use-toast";

interface SuppliersTabProps {
  shopId: string;
  inventory: any[];
}

export default function SuppliersTab({ shopId, inventory }: SuppliersTabProps) {
  // ============ ESTADOS ============
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState<any>(null);
  // ============ USAR MODAL GLOBAL DESDE SHOPINVENTORY ============
const { toast } = useToast();

const handleCreateSupplier = () => {
  const event = new CustomEvent('open-supplier-modal', {
    detail: { shopId: shopId }
  });
  window.dispatchEvent(event);
};

const handleEditSupplier = (supplier: any) => {
  const event = new CustomEvent('open-supplier-edit-modal', {
    detail: { 
      shopId: shopId,
      supplier: supplier 
    }
  });
  window.dispatchEvent(event);
};

const handleModalSuccess = () => {
  loadSuppliers();
  loadAnalysis();
};

  // ============ EFECTOS ============
  // Efecto para cargar proveedores
  useEffect(() => {
    loadSuppliers();
    loadAnalysis();
  }, [shopId]);

// ============ EFECTO PARA CALCULAR PRODUCTOS POR PROVEEDOR Y MÁRGENES ============
useEffect(() => {
  if (suppliers.length > 0 && inventory.length > 0) {
    console.log("🟣 Calculando productos por proveedor y márgenes locales...");
    
    // Agrupar productos por supplierId
    const productsBySupplier: Record<string, any[]> = {};
    
    // Calcular márgenes por proveedor localmente
    const marginsBySupplier: Record<string, { totalMargin: number; itemsWithCost: number }> = {};
    
    inventory.forEach(item => {
      if (item.supplierId) {
        // Agrupar productos
        if (!productsBySupplier[item.supplierId]) {
          productsBySupplier[item.supplierId] = [];
        }
        productsBySupplier[item.supplierId].push({
          ...item,
          product: item.product
        });
        
        // Calcular margen si tiene costo y precio válidos
        const cost = parseFloat(item.acquisitionCost || "0");
        const price = parseFloat(item.price || "0");
        
        if (cost > 0 && price > 0) {
          if (!marginsBySupplier[item.supplierId]) {
            marginsBySupplier[item.supplierId] = { totalMargin: 0, itemsWithCost: 0 };
          }
          
          const margin = ((price - cost) / cost) * 100;
          marginsBySupplier[item.supplierId].totalMargin += margin;
          marginsBySupplier[item.supplierId].itemsWithCost++;
        }
      }
    });
    
    // Actualizar suppliers SOLO si hay cambios reales
    setSuppliers(prevSuppliers => {
      const newSuppliers = prevSuppliers.map(supplier => {
        const products = productsBySupplier[supplier.id] || [];
        const marginData = marginsBySupplier[supplier.id];
        
        // Calcular margen promedio local si existe
        let localAverageMargin = supplier.averageMargin || 0;
        if (marginData && marginData.itemsWithCost > 0) {
          localAverageMargin = marginData.totalMargin / marginData.itemsWithCost;
        }
        
        return {
          ...supplier,
          products: products,
          productCount: products.length,
          averageMargin: localAverageMargin
        };
      });
      
      // Verificar si realmente cambiaron
      const hasChanges = newSuppliers.some((newSupplier, index) => {
        const oldSupplier = prevSuppliers[index];
        return newSupplier.productCount !== (oldSupplier?.productCount || 0) ||
               Math.abs(newSupplier.averageMargin - (oldSupplier?.averageMargin || 0)) > 0.1;
      });
      
      if (hasChanges) {
        console.log("🟣 Suppliers actualizados con márgenes locales:", 
          newSuppliers.map(s => `${s.name}: ${s.productCount} productos, ${s.averageMargin.toFixed(1)}%`));
      }
      
      return hasChanges ? newSuppliers : prevSuppliers;
    });
  }
}, [inventory]);

  // ============ FUNCIONES DE CARGA ============
  
  const loadSuppliers = async () => {
  try {
    console.log("🟣 loadSuppliers INICIANDO...");
    setLoading(true);
    const result = await getShopSuppliers(shopId);
    if (result.success) {
      console.log("🟣 loadSuppliers COMPLETADO - proveedores:", result.suppliers?.length || 0);
      setSuppliers(result.suppliers || []);
    }
  } catch (error: any) {
    console.error("🟣 Error cargando proveedores:", error);
    toast({
      title: "Error",
      description: "No se pudieron cargar los proveedores",
      variant: "destructive"
    });
  } finally {
    console.log("🟣 loadSuppliers FINALIZADO - setLoading(false)");
    setLoading(false);
  }
};

  const loadAnalysis = async () => {
  try {
    const result = await getSuppliersAnalysis(shopId);
    if (result.success) {
      setAnalysis(result);
      
      // 🟢 NUEVO: Actualizar suppliers con márgenes del análisis
      if (result.topSuppliersByMargin) {
        setSuppliers(prevSuppliers => {
          const updatedSuppliers = prevSuppliers.map(supplier => {
            // Buscar el margen en topSuppliersByMargin
            const marginData = result.topSuppliersByMargin.find(
              (item: any) => item.supplier?.id === supplier.id
            );
            
            // Buscar también en topSuppliersByProducts para el conteo
            const productData = result.topSuppliersByProducts.find(
              (item: any) => item.supplier?.id === supplier.id
            );
            
            return {
              ...supplier,
              averageMargin: marginData?.averageMargin || 0,
              productCount: productData?.productCount || supplier.productCount || 0
            };
          });
          
          console.log("🟢 Suppliers actualizados con márgenes:", updatedSuppliers.map(s => ({
            name: s.name,
            averageMargin: s.averageMargin,
            productCount: s.productCount
          })));
          
          return updatedSuppliers;
        });
      }
    }
  } catch (error: any) {
    console.error("Error cargando análisis:", error);
  }
};

  const handleDeleteSupplier = async (supplierId: string, supplierName: string) => {
    if (!confirm(`¿Estás seguro de que quieres eliminar al proveedor "${supplierName}"?`)) {
      return;
    }

    try {
      const result = await deleteSupplier(supplierId);
      if (result.success) {
        toast({
          title: "Proveedor eliminado",
          description: result.message,
        });
        loadSuppliers(); // Recargar la lista
      }
    } catch (error: any) {
      console.error("Error eliminando proveedor:", error);
      toast({
        title: "Error",
        description: error.message || "No se pudo eliminar el proveedor",
        variant: "destructive"
      });
    }
  };

  // ============ FUNCIONES DE EXPORTACIÓN Y CONTACTO ============

const handleExportCSV = async () => {
  try {
    toast({
      title: "Exportando...",
      description: "Generando archivo CSV de proveedores",
    });
    
    const result = await exportSuppliersCSV(shopId);
    
    if (result.success) {
      toast({
        title: "✅ Exportación completada",
        description: `Se exportaron ${suppliers.length} proveedores a CSV`,
      });
    } else {
      toast({
        title: "Error",
        description: result.error || "No se pudo exportar",
        variant: "destructive"
      });
    }
  } catch (error: any) {
    toast({
      title: "Error",
      description: error.message || "Error al exportar",
      variant: "destructive"
    });
  }
};

const handleExportWhatsApp = async () => {
  try {
    toast({
      title: "Preparando...",
      description: "Generando lista para WhatsApp",
    });
    
    // Filtrar proveedores con teléfono
    const suppliersWithPhone = suppliers.filter(s => s.phone && s.phone.trim());
    
    if (suppliersWithPhone.length === 0) {
      toast({
        title: "Sin teléfonos",
        description: "No hay proveedores con número de teléfono registrado",
        variant: "destructive"
      });
      return;
    }
    
    // Crear mensaje para WhatsApp manualmente
    let whatsappMessage = `*📋 CONTACTOS DE PROVEEDORES - ${new Date().toLocaleDateString()}*\n\n`;
    
    suppliersWithPhone.forEach((supplier, index) => {
      whatsappMessage += `*${index + 1}. ${supplier.name}*\n`;
      if (supplier.contactName) whatsappMessage += `   👤 ${supplier.contactName}\n`;
      if (supplier.phone) whatsappMessage += `   📞 ${supplier.phone}\n`;
      if (supplier.email) whatsappMessage += `   📧 ${supplier.email}\n`;
      if (supplier.productCount > 0) whatsappMessage += `   📦 ${supplier.productCount} productos\n`;
      whatsappMessage += "\n";
    });
    
    // Copiar al portapapeles
    await navigator.clipboard.writeText(whatsappMessage);
    
    // Codificar el mensaje para URL
    const encodedMessage = encodeURIComponent(whatsappMessage);
    
    toast({
      title: "✅ Listo para WhatsApp",
      description: (
        <div className="space-y-2">
          <p>Se copiaron {suppliersWithPhone.length} contactos al portapapeles</p>
          <div className="flex gap-2 mt-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`https://web.whatsapp.com/send?text=${encodedMessage}`, "_blank")}
            >
              Abrir WhatsApp Web
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => window.open(`https://wa.me/?text=${encodedMessage}`, "_blank")}
            >
              Abrir WhatsApp Móvil
            </Button>
          </div>
        </div>
      ),
      duration: 10000, // 10 segundos
    });
    
  } catch (error: any) {
    toast({
      title: "Error",
      description: error.message || "Error al preparar para WhatsApp",
      variant: "destructive"
    });
  }
};

const handleSendReminders = async () => {
  // Por ahora solo mostramos información
  toast({
    title: "Función de Recordatorios",
    description: (
      <div className="space-y-2">
        <p>Esta función permitirá enviar:</p>
        <ul className="list-disc pl-4 text-sm">
          <li>📧 Emails a proveedores seleccionados</li>
          <li>📱 Mensajes de WhatsApp automatizados</li>
          <li>🔔 Recordatorios de pagos/pedidos pendientes</li>
        </ul>
        <p className="text-xs text-muted-foreground mt-2">
          Próximamente en actualizaciones...
        </p>
      </div>
    ),
    duration: 8000,
  });
};

  // ============ CÁLCULO DE MÉTRICAS (temporal) ============
  
  // Métricas generales (usando datos del backend cuando estén disponibles)
  const metrics = {
    totalSuppliers: suppliers.length,
    productsWithSupplier: inventory.filter(item => item.supplierId).length,
    productsWithoutSupplier: inventory.filter(item => !item.supplierId).length,
    topSupplier: suppliers[0] || null
  };

  // Función para obtener el contacto de un proveedor
  const getSupplierContact = (supplier: any): string => {
    if (supplier.email && supplier.email.trim() !== '') return supplier.email;
    if (supplier.phone && supplier.phone.trim() !== '') return supplier.phone;
    if (supplier.contactName && supplier.contactName.trim() !== '') return supplier.contactName;
    return 'No especificado';
  };

  // ============ RENDERIZADO ============
  
  // Estado de carga
  if (loading && suppliers.length === 0) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
              </CardHeader>
              <CardContent>
                <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
              </CardContent>
            </Card>
          ))}
        </div>
        <div className="text-center py-12">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"></div>
          <p className="mt-4 text-muted-foreground">Cargando proveedores...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Métricas rápidas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Proveedores
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analysis?.stats?.totalSuppliers || metrics.totalSuppliers}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analysis?.stats?.activeSuppliers || suppliers.length} activos
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Productos con Proveedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {analysis?.stats?.productsWithSupplier || metrics.productsWithSupplier}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              De {inventory.length} total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sin Proveedor
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-amber-600">
              {analysis?.stats?.productsWithoutSupplier || metrics.productsWithoutSupplier}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Por asignar
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Proveedor Principal
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold truncate" title={analysis?.topSuppliersByProducts?.[0]?.supplier?.name || metrics.topSupplier?.name || 'N/A'}>
              {analysis?.topSuppliersByProducts?.[0]?.supplier?.name 
                ? analysis.topSuppliersByProducts[0].supplier.name.substring(0, 15) + 
                  (analysis.topSuppliersByProducts[0].supplier.name.length > 15 ? '...' : '')
                : metrics.topSupplier?.name
                  ? metrics.topSupplier.name.substring(0, 15) + 
                    (metrics.topSupplier.name.length > 15 ? '...' : '')
                  : 'N/A'
              }
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {analysis?.topSuppliersByProducts?.[0]?.productCount || metrics.topSupplier?.productCount || 0} productos
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Botones de acción */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div className="text-sm text-muted-foreground">
          {metrics.productsWithoutSupplier > 0 && (
            <span className="text-amber-600 font-medium">
              ⚠️ {metrics.productsWithoutSupplier} productos sin proveedor asignado
            </span>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-end">
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 sm:flex-none"
            onClick={handleExportWhatsApp}
            disabled={suppliers.length === 0}
          >
            <MessageSquare className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">WhatsApp</span>
            <span className="sm:hidden">WA</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 sm:flex-none"
            onClick={handleExportCSV}
            disabled={suppliers.length === 0}
          >
            <FileText className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Exportar CSV</span>
            <span className="sm:hidden">CSV</span>
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            className="flex-1 sm:flex-none"
            onClick={handleSendReminders}
            disabled={suppliers.length === 0}
          >
            <Bell className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Recordatorios</span>
            <span className="sm:hidden">Rec</span>
          </Button>
          <Button 
            size="sm" 
            className="flex-1 sm:flex-none"
            onClick={handleCreateSupplier}
          >
            <Truck className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Agregar Proveedor</span>
            <span className="sm:hidden">Agregar</span>
          </Button>
        </div>
      </div>

      {/* Tabla de Proveedores */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Truck className="h-5 w-5 mr-2" />
            Lista de Proveedores
          </CardTitle>
          <CardDescription>
            {suppliers.length > 0 
              ? `${suppliers.length} proveedores registrados`  // <-- Cambiado
              : 'No hay proveedores registrados. Haz clic en "Agregar Proveedor" para comenzar.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {suppliers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Proveedor</TableHead>
                    <TableHead className="hidden md:table-cell">Contacto</TableHead>
                    <TableHead>Productos</TableHead>
                    <TableHead className="hidden lg:table-cell">Margen Avg.</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                {suppliers.map((supplier, index) => {
                  // AÑADE ESTA LÍNEA PARA DEBUGGEAR:
                  console.log(`Proveedor ${index}:`, { 
                    name: supplier.name, 
                    email: supplier.email,
                    phone: supplier.phone,
                    contactName: supplier.contactName,
                    allData: supplier 
                  });
  
                  return (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        <div className="flex items-center">
                          <Truck className="h-4 w-4 mr-2 text-muted-foreground" />
                          <span className="truncate max-w-[150px]" title={supplier.name}>
                            {supplier.name}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 md:hidden mt-1">
                          {getSupplierContact(supplier)}
                        </div>
                      </TableCell>
                    <TableCell className="hidden md:table-cell">
                      <div className="flex items-center gap-1.5">
                        {supplier.email ? (
                          <Mail className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                        ) : supplier.phone ? (
                          <Phone className="h-3.5 w-3.5 text-gray-500 flex-shrink-0" />
                        ) : null}
                        <span className="text-sm text-gray-800 truncate max-w-[130px]">
                          {getSupplierContact(supplier)}
                        </span>
                      </div>
                    </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {supplier.productCount} productos
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell">
                        {supplier.averageMargin > 0 ? (
                          <div className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            supplier.averageMargin >= 30 ? "bg-green-100 text-green-800" :
                            supplier.averageMargin >= 15 ? "bg-blue-100 text-blue-800" :
                            "bg-amber-100 text-amber-800"
                          }`}>
                            <TrendingUp className="h-3 w-3 mr-1" />
                            {supplier.averageMargin.toFixed(1)}%
                          </div>
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Editar"
                            onClick={() => handleEditSupplier(supplier)} 
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Eliminar"
                            onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-8">
              <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hay proveedores registrados</h3>
              <p className="text-muted-foreground mb-4">
                Haz clic en "Agregar Proveedor" para comenzar.
              </p>
              <Button onClick={handleCreateSupplier}>  {/* <-- AÑADIR onClick */}
                <Truck className="h-4 w-4 mr-2" />
                Agregar Primer Proveedor
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Análisis de Proveedores */}
      {analysis && suppliers.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Rendimiento por Proveedor
              </CardTitle>
              <CardDescription>
                Margen promedio por proveedor
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {(analysis.topSuppliersByMargin || suppliers.filter(s => s.averageMargin > 0))
                  .slice(0, 5)
                  .map((item: any, index: number) => {
                    const supplier = item.supplier || item;
                    const avgMargin = item.averageMargin || supplier.averageMargin || 0;
                    
                    return (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-sm font-medium truncate max-w-[120px]" title={supplier.name}>
                              {supplier.name}
                            </span>
                            <span className="text-sm font-semibold">
                              {avgMargin.toFixed(1)}%
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                            <div 
                              className={`h-2 rounded-full ${
                                avgMargin >= 30 ? "bg-green-500" :
                                avgMargin >= 15 ? "bg-blue-500" :
                                "bg-amber-500"
                              }`}
                              style={{ width: `${Math.min(avgMargin, 50)}%` }}
                            ></div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <TrendingUp className="h-5 w-5 mr-2" />
                Distribución de Productos
              </CardTitle>
              <CardDescription>
                Productos por proveedor
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(() => {
                  const maxCount = Math.max(1, ...suppliers.map(s => s.productCount || 0));
                  return (analysis.topSuppliersByProducts || suppliers)
                    .slice(0, 5)
                    .map((item: any, index: number) => {
                      const supplier = item.supplier || item;
                      const productCount = item.productCount || supplier.productCount || 0;
                      
                      return (
                        <div key={index} className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-medium truncate max-w-[120px]" title={supplier.name}>
                                {supplier.name}
                              </span>
                              <span className="text-sm font-semibold">
                                {productCount}
                              </span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                              <div 
                                className="h-2 rounded-full bg-blue-500"
                                style={{ 
                                  width: `${(productCount / maxCount) * 100}%` 
                                }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

    </div>
  );
}